<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ExpandToggleButton from '@/_core/shared/ui/ExpandToggleButton/ExpandToggleButton.vue';

const DESKTOP_BREAKPOINT_QUERY = '(min-width: 1024px)';
const OVERFLOW_EPSILON_PX = 1;

const props = withDefaults(
  defineProps<{
    maxHeight?: number;
    maxDesktopHeight?: number;
    // Vue 2 v-model support for controlled expanded state.
    value?: boolean;
    // Hide the built-in toggle button (useful when a shared external toggle drives multiple Accordions).
    hideToggle?: boolean;
  }>(),
  {
    maxHeight: 150,
    maxDesktopHeight: 230,
    value: undefined,
    hideToggle: false,
  }
);

const emit = defineEmits<{
  (e: 'input', value: boolean): void;
  (e: 'overflow-change', isOverflowing: boolean): void;
}>();

const internalExpanded = ref(false);
const isControlled = computed(() => props.value !== undefined);
const isExpanded = computed(() => (isControlled.value ? !!props.value : internalExpanded.value));

function setExpanded(next: boolean) {
  emit('input', next);
  if (!isControlled.value) {
    internalExpanded.value = next;
  }
}

function toggleExpanded() {
  setExpanded(!isExpanded.value);
}

const isOverflowing = ref(false);
const contentEl = ref<HTMLElement | null>(null);

let resizeObserver: ResizeObserver | null = null;

function checkOverflow() {
  const el = contentEl.value;
  if (!el) return;

  const isDesktop = window.matchMedia(DESKTOP_BREAKPOINT_QUERY).matches;
  const threshold = isDesktop ? props.maxDesktopHeight : props.maxHeight;

  isOverflowing.value = el.scrollHeight > threshold + OVERFLOW_EPSILON_PX;
}

function handleWindowResize() {
  checkOverflow();
}

watch(isOverflowing, (next) => {
  emit('overflow-change', next);
});

onMounted(async () => {
  await nextTick();
  checkOverflow();
  emit('overflow-change', isOverflowing.value);

  if (contentEl.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(contentEl.value);
  }

  window.addEventListener('resize', handleWindowResize);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  window.removeEventListener('resize', handleWindowResize);
});
</script>

<template>
  <div>
    <div
      class="tw:flex tw:items-start tw:justify-between tw:cursor-pointer"
      @click="toggleExpanded"
    >
      <slot name="header" />
    </div>

    <div class="tw:relative">
      <div
        ref="contentEl"
        class="accordion-content"
        :class="{ expanded: isExpanded || !isOverflowing }"
        :style="{
          '--max-height': `${props.maxHeight}px`,
          '--max-desktop-height': `${props.maxDesktopHeight}px`,
        }"
      >
        <slot />
      </div>
      <div
        v-if="!isExpanded && isOverflowing"
        class="tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-12.5 tw:bg-linear-to-b tw:from-transparent tw:to-white tw:pointer-events-none"
      />
    </div>

    <div
      v-if="(isOverflowing && !props.hideToggle) || $slots.footer"
      class="tw:mt-4 tw:flex tw:min-w-0 tw:items-center tw:justify-between tw:gap-4"
    >
      <ExpandToggleButton
        v-if="isOverflowing && !props.hideToggle"
        class="tw:shrink-0"
        :value="isExpanded"
        @input="setExpanded($event)"
      />
      <div v-if="isExpanded || !isOverflowing" class="tw:flex tw:min-w-0 tw:flex-1 tw:justify-end">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.accordion-content {
  max-height: var(--max-height);
  overflow: hidden;
  transition: max-height 0.35s ease;

  @media (min-width: 1024px) {
    max-height: var(--max-desktop-height);
  }

  &.expanded {
    max-height: 2000px;
  }
}
</style>

<!-- Do NOT add anything else to this style block since it is NOT scoped -->
<style lang="scss">
.accordion-content {
  code {
    color: #3180f6;
  }
}
</style>
